import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import heroImage from '../../image/ai-instructor-hero-xr-display.png';

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: min(720px, calc(100vh - 80px));
  display: flex;
  align-items: center;
  overflow: hidden;
  background: #07171a;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 64px 0 40px;
  }
`;

const Background = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 52%;

  @media (max-width: 980px) {
    max-width: none;
    object-position: 70% center;
  }
`;

const Shade = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(5, 18, 22, 0.9) 0%, rgba(5, 18, 22, 0.72) 42%, rgba(5, 18, 22, 0.16) 100%),
    linear-gradient(180deg, rgba(5, 18, 22, 0.12) 0%, rgba(5, 18, 22, 0.52) 100%);

  @media (max-width: 768px) {
    background: linear-gradient(180deg, rgba(5, 18, 22, 0.82) 0%, rgba(5, 18, 22, 0.68) 100%);
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Content = styled.div`
  max-width: 640px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.78rem;
  font-weight: 700;
  border-radius: 999px;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.bold};
  font-size: clamp(2.15rem, 5vw, 4.25rem);
  line-height: 1.08;
  color: #fff;
  margin: 0 0 20px;
  letter-spacing: 0;
`;

const Subtitle = styled.p`
  max-width: 560px;
  font-size: 1.08rem;
  color: rgba(255, 255, 255, 0.84);
  line-height: 1.75;
  margin: 0 0 30px;

  @media (max-width: 640px) {
    font-size: 0.98rem;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    flex-direction: column;
  }
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 800;
  box-sizing: border-box;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryButton = styled(Button)`
  background: #ffffff;
  color: #005c68;
  -webkit-text-fill-color: #005c68;

  &:hover {
    background: #e6f9fc;
  }
`;

const SecondaryButton = styled(Button)`
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.42);
  background: rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.68);
  }
`;

const Metrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  max-width: 560px;
  margin-top: 46px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    margin-top: 32px;
  }
`;

const Metric = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.11);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const MetricValue = styled.div`
  color: #fff;
  font-size: 1.35rem;
  font-weight: 900;
`;

const MetricLabel = styled.div`
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.82rem;
`;

function HeroSection() {
  return (
    <Wrapper>
      <Background src={heroImage} alt="AI 강사가 디지털 강의실에서 수업하는 모습" />
      <Shade />
      <Inner>
        <Content>
          <Eyebrow>ANTSNEST DEVELOPER CENTER</Eyebrow>
          <Title>
            AI 시대에 바로 쓰는
            <br />
            실무 개발 교육
          </Title>
          <Subtitle>
            강사 중심의 라이브 수업과 프로젝트 실습으로 AI, 빅데이터, VR/AR, Web3 기술을
            업무에 적용할 수 있게 설계한 교육 플랫폼입니다.
          </Subtitle>
          <CTAGroup>
            <PrimaryButton to="/Courses">교육과정 보기</PrimaryButton>
            <SecondaryButton to="/Instructors">강사진 확인</SecondaryButton>
          </CTAGroup>
          <Metrics>
            <Metric>
              <MetricValue>10+</MetricValue>
              <MetricLabel>실무 중심 과정</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>1:1</MetricValue>
              <MetricLabel>프로젝트 피드백</MetricLabel>
            </Metric>
            <Metric>
              <MetricValue>LIVE</MetricValue>
              <MetricLabel>온라인 강의 지원</MetricLabel>
            </Metric>
          </Metrics>
        </Content>
      </Inner>
    </Wrapper>
  );
}

export default HeroSection;
